import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { votingQueryKeys } from "@/features/voting/queries/votingQueryKeys";
import votingService from "@/features/voting/services/votingService";

type CreateVotingPeriodInput = {
    candidateBookIds: string[];
    endsAt: string; // Date-only string (YYYY-MM-DD)
    startsAt: string; // Date-only string (YYYY-MM-DD)
    targetMonth: number;
    targetYear: number;
};

/**
 * Mutation hook for creating a new voting period
 * Invalidates voting queries on success and navigates to voting page
 */
export function useCreateVotingPeriod() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const mutation = useMutation({
        mutationFn: async (input: CreateVotingPeriodInput) => {
            return await votingService.createVotingPeriod(input);
        },
        onError: (error) => {
            const errorMessage = error instanceof Error ? error.message : "Failed to create voting period";
            toast.error(errorMessage);
        },
        onSuccess: () => {
            // Invalidate all voting queries
            queryClient.invalidateQueries({
                queryKey: votingQueryKeys.all,
            });

            toast.success("Voting period created successfully");
            navigate("/voting");
        },
    });

    return {
        error: mutation.error,
        isError: mutation.isError,
        isPending: mutation.isPending,
        isSuccess: mutation.isSuccess,
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
    };
}
